import { useState } from 'react';
import { dayLabel, duration, shortTime } from '../../api/attendance';
import {
  type Currency,
  DEDUCTION_LABELS,
  type Deduction,
  type EmployeePayroll,
  formatMoney,
  monthLabel,
  recentMonths,
  useAddAbsence,
  useCancelDeduction,
  useDeleteAbsence,
  useDeleteSalary,
  useEmployeePayroll,
  useRestoreDeduction,
  useSaveSalary,
} from '../../api/payroll';
import { formatIsoDate } from '../../api/records';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlay';
import { KpiCard } from '../../components/Stats';
import { ErrorState, LoadingState } from '../../components/states';

/** Сколько удержаний показывать до нажатия «Показать все». */
const VISIBLE_DEDUCTIONS = 5;

const ABSENCE_REASONS = ['Отпуск', 'Больничный', 'Командировка', 'Отгул по согласованию'];

const deductionText = (item: Deduction) =>
  item.minutes === null
    ? DEDUCTION_LABELS[item.kind]
    : `${DEDUCTION_LABELS[item.kind]} ${duration(item.minutes)}`;

function SalaryForm({
  employeeId,
  data,
  onDone,
}: {
  employeeId: string;
  data: EmployeePayroll;
  onDone: () => void;
}) {
  const current = data.salary?.current;
  const [amount, setAmount] = useState(current ? String(current.amountMinor / 100) : '');
  const [currency, setCurrency] = useState<Currency>(current?.currency ?? 'RUB');
  const [kpi, setKpi] = useState(
    data.salary?.kpiPercent !== null && data.salary?.kpiPercent !== undefined
      ? String(data.salary.kpiPercent)
      : '',
  );
  const [from, setFrom] = useState(`${data.month}-01`);
  const save = useSaveSalary(employeeId);
  const value = Number(amount.replace(/\s/g, '').replace(',', '.'));
  const kpiValue = kpi.trim() ? Number(kpi.replace(',', '.')) : null;
  const kpiValid = kpiValue === null || (Number.isFinite(kpiValue) && kpiValue >= 0 && kpiValue <= 100);
  const valid = Number.isFinite(value) && value > 0 && Boolean(from) && kpiValid;
  return (
    <form
      className="salary-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid)
          save.mutate(
            { amount: value, currency, kpiPercent: kpiValue, effectiveFrom: from },
            { onSuccess: onDone },
          );
      }}
    >
      <label className="field">
        <span>Оклад в месяц</span>
        <input
          className="input"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="например, 60 000"
        />
      </label>
      <label className="field">
        <span>Валюта</span>
        <select
          className="select"
          value={currency}
          onChange={(event) => setCurrency(event.target.value as Currency)}
        >
          <option value="RUB">Рубли</option>
          <option value="AMD">Драмы</option>
        </select>
      </label>
      <label className="field">
        <span>Премия за KPI, % от оклада</span>
        <input
          className="input"
          inputMode="decimal"
          value={kpi}
          onChange={(event) => setKpi(event.target.value)}
          placeholder="например, 20"
        />
      </label>
      <label className="field">
        <span>Действует с</span>
        <input className="input" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
      </label>
      <div className="salary-form-actions">
        <button type="submit" className="btn-primary" disabled={!valid || save.isPending}>
          Сохранить
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>
          Отмена
        </button>
      </div>
      {save.isError ? <ErrorState title="Оклад не сохранён" error={save.error} /> : null}
    </form>
  );
}

function CancelDialog({
  employeeId,
  item,
  onClose,
}: {
  employeeId: string;
  item: Deduction;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useCancelDeduction(employeeId);
  return (
    <Modal title={`Снять удержание · ${dayLabel(item.date)}`} onClose={onClose}>
      <p>
        {deductionText(item)} — {formatMoney(item.amountMinor, item.currency)}. Удержание останется в списке
        зачёркнутым, с причиной.
      </p>
      <label className="field">
        <span>Причина</span>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Например: командировка, согласовано с руководителем, сбой турникета"
        />
      </label>
      <div className="salary-form-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={reason.trim().length < 3 || cancel.isPending}
          onClick={() =>
            cancel.mutate({ date: item.date, kind: item.kind, reason: reason.trim() }, { onSuccess: onClose })
          }
        >
          Снять удержание
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Отмена
        </button>
      </div>
      {cancel.isError ? <ErrorState title="Не получилось" error={cancel.error} /> : null}
    </Modal>
  );
}

function AbsenceDialog({
  employeeId,
  month,
  onClose,
}: {
  employeeId: string;
  month: string;
  onClose: () => void;
}) {
  const [from, setFrom] = useState(`${month}-01`);
  const [to, setTo] = useState(`${month}-01`);
  const [preset, setPreset] = useState(ABSENCE_REASONS[0] ?? '');
  const [note, setNote] = useState('');
  const add = useAddAbsence(employeeId);
  const reason = note.trim() ? `${preset}: ${note.trim()}` : preset;
  return (
    <Modal title="Отметить отсутствие" onClose={onClose}>
      <p className="muted">За эти дни не будет ни прогулов, ни опозданий, ни раннего ухода.</p>
      <div className="salary-form">
        <label className="field">
          <span>С</span>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="field">
          <span>По</span>
          <input className="input" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <label className="field">
          <span>Причина</span>
          <select className="select" value={preset} onChange={(event) => setPreset(event.target.value)}>
            {ABSENCE_REASONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span>Комментарий (необязательно)</span>
        <input className="input" value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      <div className="salary-form-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={!from || !to || to < from || add.isPending}
          onClick={() => add.mutate({ from, to, reason }, { onSuccess: onClose })}
        >
          Сохранить
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Отмена
        </button>
      </div>
      {to && from && to < from ? <p className="text-bad">Дата окончания раньше даты начала</p> : null}
      {add.isError ? <ErrorState title="Не сохранено" error={add.error} /> : null}
    </Modal>
  );
}

/** Состояние запроса оклада: 403 — оклад этому человеку не виден, раздел просто не показывается. */
const isForbidden = (error: unknown) => (error as { status?: number } | null)?.status === 403;

/**
 * Сводка «Оклад и KPI» за месяц: оклад, премия за KPI сверх оклада (оклад × % KPI × выполнение),
 * удержания и нарушения, итог. Оклад видят сам сотрудник, его руководители, HR и администратор.
 */
export function SalarySummary({ employeeId, month }: { employeeId: string; month: string }) {
  const [editing, setEditing] = useState(false);
  const payroll = useEmployeePayroll(employeeId, month);
  const removeSalary = useDeleteSalary(employeeId);
  const data = payroll.data;
  if (payroll.isError) return isForbidden(payroll.error) ? null : <ErrorState error={payroll.error} />;
  if (!data?.salary) return null;
  const salary = data.salary;
  const currency = salary.current?.currency ?? data.totals.currency;
  const withheld = data.totals.deductedMinor + (salary.finesMinor ?? 0);

  return (
    <div className="salary-summary">
      <h3 className="salary-summary-title">{monthLabel(month)}</h3>
      {salary.current ? (
        <div className="kpi-grid five payroll-cards">
          <KpiCard
            label="Оклад"
            icon="wallet"
            value={formatMoney(salary.current.amountMinor, salary.current.currency)}
            extra={
              salary.accrual && salary.accrual.amountMinor !== salary.current.amountMinor
                ? `за месяц ${formatMoney(salary.accrual.amountMinor, salary.accrual.currency)}`
                : `с ${formatIsoDate(salary.current.effectiveFrom)}`
            }
          />
          <KpiCard
            label="Выполнение KPI"
            icon="activity"
            value={
              salary.kpi?.total !== null && salary.kpi?.total !== undefined ? `${salary.kpi.total} %` : '—'
            }
            extra={
              salary.kpiPercent !== null
                ? `премия ${salary.kpiPercent}% от оклада`
                : 'процент премии не задан'
            }
          />
          <KpiCard
            label="Премия за KPI"
            icon="check"
            tone={salary.kpi?.premiumMinor ? 'good' : undefined}
            value={
              salary.kpi?.premiumMinor !== null && salary.kpi?.premiumMinor !== undefined
                ? formatMoney(salary.kpi.premiumMinor, currency)
                : '—'
            }
          />
          <KpiCard
            label="Удержания и нарушения"
            icon="alert"
            tone={withheld ? 'bad' : undefined}
            value={formatMoney(withheld, currency)}
            extra={
              salary.finesMinor ? `из них нарушения ${formatMoney(salary.finesMinor, currency)}` : undefined
            }
          />
          <KpiCard
            label="Итого за месяц"
            icon="calendar"
            value={salary.totalMinor !== null ? formatMoney(salary.totalMinor, currency) : '—'}
          />
        </div>
      ) : (
        <p className="muted">Оклад не назначен.</p>
      )}
      {data.canManage ? (
        editing ? (
          <SalaryForm employeeId={employeeId} data={data} onDone={() => setEditing(false)} />
        ) : (
          <div className="payroll-actions">
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              <Icon name="pen" size={14} />
              {salary.current ? 'Изменить оклад и % KPI' : 'Назначить оклад'}
            </button>
            {salary.history.length ? (
              <details className="salary-history">
                <summary>История оклада</summary>
                <ul>
                  {salary.history.map((row) => (
                    <li key={row.effectiveFrom} className="history-row">
                      с {formatIsoDate(row.effectiveFrom)} — {formatMoney(row.amountMinor, row.currency)}
                      {row.kpiPercent !== null ? `, KPI ${row.kpiPercent}%` : ''}
                      {row.updatedBy ? <small> · {row.updatedBy}</small> : null}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => {
                          if (window.confirm(`Удалить оклад с ${formatIsoDate(row.effectiveFrom)}?`))
                            removeSalary.mutate(row.effectiveFrom);
                        }}
                      >
                        удалить
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * Удержания по данным системы проходов — часть раздела «Нарушения»: опоздания, ранний уход,
 * прогулы за месяц, снятие удержаний и отметки об отпуске, больничном, командировке.
 */
export function AttendanceDeductions({
  employeeId,
  currentMonth,
}: {
  employeeId: string;
  currentMonth: string;
}) {
  const months = recentMonths(currentMonth);
  const [month, setMonth] = useState(currentMonth);
  const [cancelling, setCancelling] = useState<Deduction | null>(null);
  const [absence, setAbsence] = useState(false);
  // Список удержаний свёрнут: сразу видны последние, остальное — по кнопке.
  const [expanded, setExpanded] = useState(false);
  const payroll = useEmployeePayroll(employeeId, month);
  const restore = useRestoreDeduction(employeeId);
  const removeAbsence = useDeleteAbsence(employeeId);
  const data = payroll.data;
  const currency = data?.totals.currency ?? null;
  const schedule = data?.schedule;
  if (payroll.isError && isForbidden(payroll.error)) return null;

  return (
    <div className="reg-part attendance-deductions">
      <div className="payroll-absences-head">
        <h3>Опоздания, ранний уход, прогулы</h3>
        {data?.hasSalary ? (
          <span className={`status ${data.totals.deductedMinor ? 'status-bad' : 'status-ok'}`}>
            {data.totals.deductedMinor ? formatMoney(data.totals.deductedMinor, currency) : 'Без удержаний'}
          </span>
        ) : null}
      </div>
      <fieldset className="pill-row period-pills">
        <legend className="visually-hidden">Месяц</legend>
        {months.map((item) => (
          <button
            key={item}
            type="button"
            className="pill-toggle"
            aria-pressed={item === month}
            onClick={() => setMonth(item)}
          >
            {monthLabel(item)}
          </button>
        ))}
      </fieldset>

      {payroll.isPending ? <LoadingState label="Считаю удержания" /> : null}
      {payroll.isError ? <ErrorState error={payroll.error} onRetry={() => void payroll.refetch()} /> : null}

      {data ? (
        <>
          {!data.hasSalary ? (
            <p className="notice">
              <Icon name="alert" size={14} />
              Оклад в системе не назначен — удержания не считаются.
            </p>
          ) : null}
          {!data.linked ? (
            <p className="notice">
              <Icon name="alert" size={14} />
              Сотрудник не связан с системой проходов: опозданий и прогулов по турникету нет.
            </p>
          ) : null}

          <p className="payroll-rules muted">
            Опоздание и ранний уход — по минутам из дневной ставки, прогул — дневная ставка, но не больше
            ставки за день.
            {schedule?.workStart || schedule?.workEnd
              ? ` График: ${shortTime(schedule.workStart ?? null)}–${schedule.workEnd ? shortTime(schedule.workEnd) : '…'}${
                  schedule.lateAfter ? `, опоздание после ${shortTime(schedule.lateAfter)}` : ''
                }.`
              : ' График не задан: опоздания не считаются.'}
            {data.holidays.length
              ? ` Нерабочие дни: ${data.holidays.map((item) => `${formatIsoDate(item.day).slice(0, 5)} ${item.title}`).join(', ')}.`
              : ''}
          </p>

          {data.items.length ? (
            <>
              <div className="table-scroll">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>День</th>
                      <th>Что</th>
                      <th className="number">Сумма</th>
                      {data.canManage ? (
                        <th>
                          <span className="visually-hidden">Действия</span>
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {(expanded ? data.items : data.items.slice(0, VISIBLE_DEDUCTIONS)).map((item) => (
                      <tr
                        key={`${item.date}-${item.kind}`}
                        className={item.cancelled ? 'cancelled-row' : undefined}
                      >
                        <td>
                          <strong>{dayLabel(item.date)}</strong>
                        </td>
                        <td>
                          {deductionText(item)}
                          {item.cancelled ? (
                            <small>
                              снято: {item.cancelled.reason}
                              {item.cancelled.by ? ` · ${item.cancelled.by}` : ''}
                            </small>
                          ) : null}
                        </td>
                        <td className={`number${item.cancelled ? '' : ' bad'}`}>
                          {item.cancelled ? (
                            <s>{formatMoney(item.amountMinor, item.currency)}</s>
                          ) : (
                            formatMoney(item.amountMinor, item.currency)
                          )}
                        </td>
                        {data.canManage ? (
                          <td className="link-cell">
                            {item.cancelled ? (
                              <button
                                type="button"
                                className="btn-link"
                                disabled={restore.isPending}
                                onClick={() => restore.mutate({ date: item.date, kind: item.kind })}
                              >
                                Вернуть
                              </button>
                            ) : (
                              <button type="button" className="btn-link" onClick={() => setCancelling(item)}>
                                Снять
                              </button>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.items.length > VISIBLE_DEDUCTIONS ? (
                <button
                  type="button"
                  className="btn-secondary more-row"
                  onClick={() => setExpanded(!expanded)}
                >
                  <Icon name={expanded ? 'minus' : 'plus'} size={14} />
                  {expanded ? 'Свернуть' : `Показать все — ещё ${data.items.length - VISIBLE_DEDUCTIONS}`}
                </button>
              ) : null}
            </>
          ) : data.hasSalary ? (
            <p className="fine-none">
              <Icon name="check" size={16} />
              За {monthLabel(month)} удержаний нет
            </p>
          ) : null}

          <div className="payroll-absences">
            <div className="payroll-absences-head">
              <h3>Отпуск, больничный, командировка</h3>
              {data.canExcuse ? (
                <button type="button" className="btn-secondary" onClick={() => setAbsence(true)}>
                  <Icon name="plus" size={14} />
                  Отметить отсутствие
                </button>
              ) : null}
            </div>
            {data.excused.length ? (
              <ul className="payroll-absence-list">
                {data.excused.map((item) => (
                  <li key={item.id} className="absence-row">
                    <strong>
                      {formatIsoDate(item.dateFrom)}
                      {item.dateTo !== item.dateFrom ? ` — ${formatIsoDate(item.dateTo)}` : ''}
                    </strong>
                    <span>{item.reason}</span>
                    {item.createdBy ? <small className="muted">{item.createdBy}</small> : null}
                    {data.canExcuse ? (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => {
                          if (window.confirm('Убрать отметку об отсутствии? Удержания за эти дни вернутся.'))
                            removeAbsence.mutate(item.id);
                        }}
                      >
                        убрать
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">За {monthLabel(month)} отметок нет</p>
            )}
          </div>

          {cancelling ? (
            <CancelDialog employeeId={employeeId} item={cancelling} onClose={() => setCancelling(null)} />
          ) : null}
          {absence ? (
            <AbsenceDialog employeeId={employeeId} month={month} onClose={() => setAbsence(false)} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
